import React, {useEffect, useState} from 'react';
import {Box, Dialog, DialogTitle, IconButton, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Button from '@mui/material/Button';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import CircularProgress from '@mui/material/CircularProgress';

const SUPPORTED_MIME_TYPES = {
    'application/pdf': 'pdf',
    'image/*': 'image',
};

const resolveMimeType = type => {
    if (SUPPORTED_MIME_TYPES[type]) {
        return SUPPORTED_MIME_TYPES[type];
    }

    const [mainType] = type.split('/');
    const wildcardKey = `${mainType}/*`;

    if (SUPPORTED_MIME_TYPES[wildcardKey]) {
        return SUPPORTED_MIME_TYPES[wildcardKey];
    }

    return null;
};

const assetRenderers = {
    pdf: ({url}) => {
        return (
            <iframe
                src={`${url}#toolbar=0&navpanes=0`}
                title="PDF Preview"
                width="100%"
                height="100%"
                style={{
                    border: 'none',
                    overflow: 'hidden'
                }}
            />
        );
    },
    image: ({url, onError}) => (
        <Box
            component="img"
            src={url}
            alt="Preview"
            sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
            }}
            onError={onError}
        />
    ),
    default: () => null
};

const ASSET_ERRORS = {
    NO_ASSET: 'No asset provided',
    INVALID_FORMAT: 'Invalid asset format',
    UNSUPPORTED_TYPE: 'Unsupported asset type',
};

/* eslint-disable react/prop-types */

const DocumentPreviewDialog = ({
                                   open,
                                   onClose,
                                   document,
                                   documentName,
                                   isLoading,
                                   maxWidth = 'md'
                               }) => {
    const [assetUrl, setAssetUrl] = useState('');
    const [assetType, setAssetType] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        const setupAsset = () => {
            setError(null);

            if (!document) {
                setError(ASSET_ERRORS.NO_ASSET);
                return;
            }

            if (!(document instanceof Blob)) {
                setError(ASSET_ERRORS.INVALID_FORMAT);
                return;
            }

            const type = resolveMimeType(document.type);

            if (!type) {
                setError(ASSET_ERRORS.UNSUPPORTED_TYPE);
                return;
            }

            setAssetUrl(URL.createObjectURL(document));
            setAssetType(type);
        };

        if (open && !isLoading) {
            setupAsset();
        }

        return () => {
            setAssetUrl('');
            setAssetType('');
            setError(null);
        };
    }, [document, open]);

    const renderContent = () => {
        if (isLoading) {
            return <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                height="100%"
            >
                <CircularProgress size={36}></CircularProgress>
            </Box>;
        }

        if (error) {
            return (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={2}
                    paddingTop={2}
                >
                    <ErrorOutlineIcon color="error" sx={{fontSize: 48}}/>
                    <Typography color="error">{error}</Typography>
                    {
                        error === ASSET_ERRORS.UNSUPPORTED_TYPE && document
                            ? <Button
                                endIcon={<DownloadRoundedIcon/>}
                                href={URL.createObjectURL(document)}
                                download={documentName}
                            >
                                Download to view
                            </Button>
                            : null
                    }
                </Box>
            );
        }

        const Renderer = assetRenderers[assetType] || assetRenderers.default;

        return Renderer({
            url: assetUrl,
            onError: () => setError('Failed to load asset'),
            title: documentName
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={maxWidth}
            fullWidth
            fullScreen
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >{documentName || ''}</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </Box>
            </DialogTitle>
            {renderContent()}
        </Dialog>
    );
};

export default DocumentPreviewDialog;